import React, { useEffect } from 'react';
import { Button, Col, Form, Input, Modal, Row, Select } from 'antd';
import { canManageSupplierVisibility } from '../config/permissions';
import { COUNTRIES } from '../constants/countries';
import { CURRENCIES } from '../constants/currencies';
import { useI18n } from '../i18n';

const { Option } = Select;

export default function SupplierModal({
  open,
  onCancel,
  onSubmit,
  editingSupplier,
  currentUser,
}) {
  const [form] = Form.useForm();
  const isEditing = Boolean(editingSupplier);
  const { t } = useI18n();
  const canEditVisibility = canManageSupplierVisibility(currentUser);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: editingSupplier?.name || '',
      visibility: canEditVisibility ? (editingSupplier?.visibility || 'Public') : 'Public',
      supplierType: editingSupplier?.supplierType || 'Product',
      cvr: editingSupplier?.cvr || '',
      address: editingSupplier?.address || '',
      country: editingSupplier?.country || 'Denmark',
      currency: editingSupplier?.currency || 'DKK',
      phone: editingSupplier?.phone || '',
      email: editingSupplier?.email || '',
      website: editingSupplier?.website || '',
    });
  }, [canEditVisibility, editingSupplier, form, open]);

  const handleFinish = async () => {
    const values = await form.validateFields();
    onSubmit(values);
  };

  return (
    <Modal
      title={isEditing ? t('Edit Supplier') : t('Add New Supplier')}
      open={open}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel}>{t('Close')}</Button>,
        <Button key="submit" type="primary" onClick={handleFinish}>
          {isEditing ? t('Update') : t('Create')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t('Supplier Name')}
          name="name"
          rules={[{ required: true, message: t('Please enter supplier name') }]}
        >
          <Input placeholder={t('Supplier name')} />
        </Form.Item>
        <Form.Item
          label={t('Visibility')}
          name="visibility"
          rules={[{ required: true, message: t('Please select visibility') }]}
        >
          <Select disabled={!canEditVisibility}>
            <Option value="Public">{t('Public')}</Option>
            <Option value="Private">{t('Private')}</Option>
          </Select>
        </Form.Item>
        <Form.Item label={t('CVR Number')} name="cvr">
          <Input placeholder={t('CVR number')} />
        </Form.Item>
        <Form.Item label={t('Address')} name="address">
          <Input placeholder={t('Full address')} />
        </Form.Item>
        <Form.Item label={t('Country')} name="country" rules={[{ required: true, message: t('Please select country') }]}>
          <Select>
            {COUNTRIES.map((option) => (
              <Option key={option} value={option}>{t(option)}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label={t('Default Currency')} name="currency" rules={[{ required: true, message: t('Please select currency') }]}>
          <Select>
            {CURRENCIES.map((option) => (
              <Option key={option} value={option}>{t(option)}</Option>
            ))}
          </Select>
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('Contact Phone')} name="phone">
              <Input placeholder="+45 12 34 56 78" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('Contact Email')}
              name="email"
              rules={[
                {
                  type: 'email',
                  message: t('Please enter a valid email address'),
                },
              ]}
            >
              <Input placeholder="contact@supplier.dk" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label={t('Website')}
          name="website"
          rules={[
            {
              type: 'url',
              message: t('Please enter a valid website URL'),
            },
          ]}
        >
          <Input placeholder="https://www.supplier.dk" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
